/**
 * Shared form primitives for the Hub UI.
 *
 * Convention: pass a pure `validate?: (value) => string | null` from the
 * validators in `packages/hub/ui/src/lib/validation.ts`. The primitive then owns
 * the on-blur / on-change-after-error feedback loop: nothing shows until
 * the field is blurred, and once it's flagged, every keystroke re-checks
 * so the user sees the error clear the instant the value becomes valid.
 *
 * For submit-side feedback the page calls its aggregate validator,
 * passes the resulting `error` prop (which overrides the primitive's
 * internal state), uses `focusFirstInvalid(formElement)` to land the
 * cursor on the first failing field, and renders `<FormErrorSummary>`
 * inside the form for the screen-reader announcement. Pages that don't
 * pass `validate` keep the legacy "error prop owns everything" shape;
 * the new behaviour is opt-in per field.
 */

import { AlertTriangle, CheckCircle2, Eye, EyeOff, Info, XCircle } from "lucide-react";
import type { KeyboardEventHandler, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useId, useState } from "react";
import { NativeSelect } from "./native-select";

const FIELD_INPUT_CLASS =
  "block h-[var(--field-h)] w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-text)] transition-[color,background-color,border-color] duration-100 placeholder:text-[var(--color-text-tertiary)] hover:border-[var(--color-border-strong)] disabled:cursor-not-allowed disabled:bg-[var(--color-surface-2)] disabled:text-[var(--color-text-tertiary)] aria-[invalid=true]:border-[var(--color-danger)]";

interface FieldFrameProps {
  children: ReactNode;
  counter?: string;
  error?: string;
  errorId?: string;
  helperId?: string;
  inputId: string;
  label: string;
  helper?: string;
}

export function FieldFrame({
  children,
  counter,
  error,
  errorId,
  helperId,
  inputId,
  label,
  helper,
}: FieldFrameProps) {
  return (
    <div className="grid gap-1.5">
      <span className="flex items-center justify-between gap-3">
        <label className="type-label text-[var(--color-text)]" htmlFor={inputId}>
          {label}
        </label>
        {counter ? (
          <span className="field-helper type-meta text-[var(--color-text-tertiary)]">
            {counter}
          </span>
        ) : null}
      </span>
      {children}
      {error ? (
        <span className="field-error type-meta text-[var(--color-danger)]" id={errorId}>
          {error}
        </span>
      ) : null}
      {/* Helper keeps a stable id and stays mounted whenever it's supplied, so
          aria-describedby can point at it on first focus instead of only after
          an error appears. When an error is also showing, the error owns the
          visible slot and the helper drops to sr-only — still announced, not
          stacked beneath the error. */}
      {helper ? (
        <span
          className={error ? "sr-only" : "field-helper type-meta text-[var(--color-text-tertiary)]"}
          id={helperId}
        >
          {helper}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Builds the per-field id set and the `aria-describedby` value. Helper text is
 * linked whenever it exists — not just on error — and sits ahead of the error
 * id when both are present, so a screen reader hears the guidance before the
 * failure. Returns `undefined` for absent ids so the value spreads cleanly onto
 * a control (an empty `aria-describedby` would otherwise reference nothing).
 */
function fieldDescription(
  id: string,
  hasError: boolean,
  hasHelper: boolean,
): { errorId: string | undefined; helperId: string | undefined; describedBy: string | undefined } {
  const errorId = hasError ? `${id}-error` : undefined;
  const helperId = hasHelper ? `${id}-helper` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;
  return { errorId, helperId, describedBy };
}

/**
 * Whether to default `spellCheck` off for a text-style control. Credential and
 * machine-token fields (passwords, emails, usernames, one-time codes) carry no
 * prose, so browser spellcheck only paints distracting red squiggles and can
 * leak secret characters to a spellcheck service. Callers can still pass an
 * explicit `spellCheck` to override. Returns `undefined` for ordinary prose
 * fields so the browser default stands.
 */
function defaultSpellCheck(
  type: string | undefined,
  autoComplete: string | undefined,
): false | undefined {
  if (type === "email" || type === "password") return false;
  if (autoComplete && /username|password|one-time-code|email/.test(autoComplete)) return false;
  return undefined;
}

/**
 * Internal hook used by the text-style primitives to drive the
 * on-blur / on-change-after-error pattern. When `validate` is omitted the
 * hook is inert and the parent's `error` prop is the only source of
 * truth — so primitives stay backwards-compatible for pages that haven't
 * adopted the validator API yet.
 *
 * Precedence once a validator is supplied: the *client-side* check is
 * authoritative once the field is touched. If the validator says the
 * value is fine, the field reads as valid even when the parent still
 * holds a stale submit-time error in its `errors` state — that's what
 * makes the inline error clear as the user types a fix. Pages that
 * surface a non-client-checkable rejection (e.g. "email already taken")
 * should drop the matching `validate` prop so the external `error`
 * remains the only signal.
 */
function useFieldValidation(
  value: string,
  externalError: string | undefined,
  validate: ((value: string) => string | null) | undefined,
): { error: string | undefined; onBlur: () => void } {
  const [touched, setTouched] = useState(false);
  // When a parent sets an external error (e.g. a submit-time guard
  // populating the page's `errors` state), treat the field as
  // already-touched so the validator drives subsequent keystrokes.
  useEffect(() => {
    if (externalError) setTouched(true);
  }, [externalError]);

  if (!validate) return { error: externalError, onBlur: () => setTouched(true) };
  if (!touched) return { error: externalError, onBlur: () => setTouched(true) };
  return {
    error: validate(value) ?? undefined,
    onBlur: () => setTouched(true),
  };
}

interface TextFieldProps {
  /**
   * Marks the input as the overlay's preferred initial-focus target. The
   * shared substrate prefers it over the first non-dismissal control.
   */
  "data-autofocus"?: "true";
  disabled?: boolean;
  error?: string;
  helper?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  label: string;
  maxLength?: number;
  onChange(value: string): void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  autoComplete?: string;
  pattern?: string;
  placeholder?: string;
  required?: boolean;
  spellCheck?: boolean;
  title?: string;
  type?: "text" | "password" | "url" | "email" | "time" | "number";
  validate?: (value: string) => string | null;
  value: string;
}

export function TextField({
  autoComplete,
  "data-autofocus": dataAutofocus,
  disabled,
  error,
  helper,
  inputRef,
  label,
  maxLength,
  onChange,
  onKeyDown,
  pattern,
  placeholder,
  required,
  spellCheck,
  title,
  type = "text",
  validate,
  value,
}: TextFieldProps) {
  const id = useId();
  const { error: shownError, onBlur } = useFieldValidation(value, error, validate);
  const { errorId, helperId, describedBy } = fieldDescription(id, !!shownError, !!helper);
  return (
    <FieldFrame
      error={shownError}
      errorId={errorId}
      helperId={helperId}
      inputId={id}
      label={label}
      helper={helper}
    >
      <input
        aria-describedby={describedBy}
        aria-invalid={shownError ? "true" : "false"}
        autoComplete={autoComplete}
        className={FIELD_INPUT_CLASS}
        data-autofocus={dataAutofocus}
        disabled={disabled}
        id={id}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        pattern={pattern}
        placeholder={placeholder}
        ref={inputRef}
        required={required}
        spellCheck={spellCheck ?? defaultSpellCheck(type, autoComplete)}
        title={title}
        type={type}
        value={value}
      />
    </FieldFrame>
  );
}

interface PasswordFieldProps {
  autoComplete?: string;
  disabled?: boolean;
  error?: string;
  helper?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  label: string;
  onChange(value: string): void;
  // Secrets carry no prose; spellcheck only squiggles and risks leaking the
  // value to a spellcheck service. Off by default, overridable per caller.
  spellCheck?: boolean;
  validate?: (value: string) => string | null;
  value: string;
}

export function PasswordField({
  autoComplete = "off",
  disabled,
  error,
  helper,
  inputRef,
  label,
  onChange,
  spellCheck = false,
  validate,
  value,
}: PasswordFieldProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const { error: shownError, onBlur } = useFieldValidation(value, error, validate);
  const { errorId, helperId, describedBy } = fieldDescription(id, !!shownError, !!helper);
  const Icon = visible ? EyeOff : Eye;

  return (
    <FieldFrame
      error={shownError}
      errorId={errorId}
      helper={helper}
      helperId={helperId}
      inputId={id}
      label={label}
    >
      <div className="relative">
        <input
          aria-describedby={describedBy}
          aria-invalid={shownError ? "true" : "false"}
          autoComplete={autoComplete}
          className={`${FIELD_INPUT_CLASS} pr-10`}
          disabled={disabled}
          id={id}
          onBlur={onBlur}
          onChange={(event) => onChange(event.target.value)}
          ref={inputRef}
          spellCheck={spellCheck}
          type={visible ? "text" : "password"}
          value={value}
        />
        <button
          aria-label={visible ? "Hide value" : "Show value"}
          className="absolute right-1 top-1/2 inline-flex size-[var(--space-7)] -translate-y-1/2 items-center justify-center rounded-md text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)]"
          disabled={disabled}
          onClick={() => setVisible((current) => !current)}
          type="button"
        >
          <Icon aria-hidden="true" className="size-4" />
        </button>
      </div>
    </FieldFrame>
  );
}

interface TextAreaFieldProps {
  counter?: string;
  disabled?: boolean;
  error?: string;
  helper?: string;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  label: string;
  maxLength?: number;
  onChange(value: string): void;
  placeholder?: string;
  rows: number;
  validate?: (value: string) => string | null;
  value: string;
}

export function TextAreaField({
  counter,
  disabled,
  error,
  helper,
  inputRef,
  label,
  maxLength,
  onChange,
  placeholder,
  rows,
  validate,
  value,
}: TextAreaFieldProps) {
  const id = useId();
  const { error: shownError, onBlur } = useFieldValidation(value, error, validate);
  const { errorId, helperId, describedBy } = fieldDescription(id, !!shownError, !!helper);
  return (
    <FieldFrame
      counter={counter}
      error={shownError}
      errorId={errorId}
      helper={helper}
      helperId={helperId}
      inputId={id}
      label={label}
    >
      <textarea
        aria-describedby={describedBy}
        aria-invalid={shownError ? "true" : "false"}
        className="block w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-sm leading-6 text-[var(--color-text)] transition-[color,background-color,border-color] duration-100 placeholder:text-[var(--color-text-tertiary)] hover:border-[var(--color-border-strong)] disabled:cursor-not-allowed disabled:bg-[var(--color-surface-2)] disabled:text-[var(--color-text-tertiary)] aria-[invalid=true]:border-[var(--color-danger)]"
        disabled={disabled}
        id={id}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        ref={inputRef}
        rows={rows}
        value={value}
      />
    </FieldFrame>
  );
}

interface SelectFieldProps {
  disabled?: boolean;
  error?: string;
  helper?: string;
  inputRef?: RefObject<HTMLSelectElement | null>;
  label: string;
  onChange(value: string): void;
  options: readonly { value: string; label: string }[] | readonly string[];
  validate?: (value: string) => string | null;
  value: string;
}

export function SelectField({
  disabled,
  error,
  helper,
  inputRef,
  label,
  onChange,
  options,
  validate,
  value,
}: SelectFieldProps) {
  const id = useId();
  const { error: shownError, onBlur } = useFieldValidation(value, error, validate);
  const { errorId, helperId, describedBy } = fieldDescription(id, !!shownError, !!helper);
  return (
    <FieldFrame
      error={shownError}
      errorId={errorId}
      helper={helper}
      helperId={helperId}
      inputId={id}
      label={label}
    >
      <NativeSelect
        aria-describedby={describedBy}
        aria-invalid={shownError ? "true" : "false"}
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        ref={inputRef}
        value={value}
      >
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const label = typeof option === "string" ? option : option.label;
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
      </NativeSelect>
    </FieldFrame>
  );
}

const PANEL_MESSAGE_TONES = {
  info: { icon: Info, style: "bg-(--color-info-soft) text-(--color-info)" },
  success: { icon: CheckCircle2, style: "bg-(--color-success-soft) text-(--color-success)" },
  warning: { icon: AlertTriangle, style: "bg-(--color-warning-soft) text-(--color-warning)" },
  danger: { icon: XCircle, style: "bg-(--color-danger-soft) text-(--color-danger)" },
} as const;

export type PanelMessageTone = "neutral" | keyof typeof PANEL_MESSAGE_TONES;

interface PanelMessageProps {
  children: ReactNode;
  tone?: PanelMessageTone;
  title?: string;
  action?: ReactNode;
}

export function PanelMessage({ children, tone = "neutral", title, action }: PanelMessageProps) {
  if (tone !== "neutral") {
    const { icon: Icon, style } = PANEL_MESSAGE_TONES[tone];
    return (
      <div
        role={tone === "danger" ? "alert" : "status"}
        className={`flex items-start gap-(--space-3) rounded-(--radius-sm) border border-(--color-border) p-(--space-4) type-body ${style}`}
      >
        <Icon aria-hidden="true" className="mt-(--space-1) size-4 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-(--space-3)">
          <div className="min-w-0 flex-1 basis-auto [overflow-wrap:anywhere]">
            {title ? <p className="mb-(--space-1) font-medium">{title}</p> : null}
            <div className="text-(--color-text)">{children}</div>
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      </div>
    );
  }
  return (
    <div
      className="px-[var(--layout-card-pad-x)] py-[var(--empty-state-py-inline)] text-sm text-(--color-text-secondary)"
      role="status"
    >
      {title ? <p className="mb-(--space-1) font-medium">{title}</p> : null}
      {children}
      {action}
    </div>
  );
}

interface FormErrorSummaryProps {
  /**
   * Per-field error messages collected from the page's aggregate validator
   * on submit. Empty array (or zero messages) renders nothing, so the
   * `aria-live` region stays silent until there's a real announcement.
   */
  messages: readonly string[];
  /** Optional override for the announcement preamble. */
  title?: string;
}

/**
 * Renders a visually-hidden `aria-live="polite"` region after a failed
 * submit so screen readers announce the list of validation errors in
 * one pass. The visible per-field errors remain owned by each field
 * primitive — this is the summary-only channel for assistive tech.
 */
export function FormErrorSummary({ messages, title = "Form has errors" }: FormErrorSummaryProps) {
  return (
    <div aria-live="polite" className="sr-only" role="status">
      {messages.length > 0 ? (
        <>
          <p>{title}</p>
          <ul>
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * Finds the first descendant with `aria-invalid="true"` inside `form` and
 * focuses it. Pages call this after their aggregate validator surfaces
 * errors so the cursor lands on the first failing field. Returns the
 * focused element so callers can decide whether to also scroll or not.
 */
export function focusFirstInvalid(form: HTMLElement | null): HTMLElement | null {
  if (!form) return null;
  const candidate = form.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (candidate) candidate.focus();
  return candidate;
}

/**
 * Failed-submit focus helper. Returns a trigger the submit handler calls
 * synchronously; the actual `focusFirstInvalid(scope)` runs post-commit (via
 * a nonce-keyed effect) so it still lands on a field whose `aria-invalid`
 * marker only renders in the same commit as the new errors. Re-triggering on
 * a repeat failed submit re-focuses the first invalid field.
 */
export function useFocusFirstInvalid(scope: RefObject<HTMLElement | null>): () => void {
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (nonce > 0) focusFirstInvalid(scope.current);
  }, [nonce, scope]);
  return useCallback(() => setNonce((current) => current + 1), []);
}

/**
 * Helper for pages whose aggregate validator returns an `errors` object
 * keyed by field name. Returns the unique non-empty messages in field
 * order, suitable for `<FormErrorSummary messages={...} />`. Accepts
 * any object whose values are `string | undefined` so per-page error
 * interfaces (which omit the index signature) still satisfy the shape.
 */
export function collectErrorMessages<T extends object>(errors: T): string[] {
  const seen = new Set<string>();
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (typeof value !== "string" || value.length === 0) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    messages.push(value);
  }
  return messages;
}
